import { computed, ref } from 'vue';

import axios from '@/plugins/axios.js';

const optionsCache = ref(null);
const isLoading = ref(false);
const loadingError = ref(null);

const lookupFromCategories = categories => {
    const lookup = {};

    (categories || []).forEach(category => {
        (category.subcategories || []).forEach(subcategory => {
            (subcategory.options || []).forEach(option => {
                lookup[option.key] = option.label;
            });
        });
    });

    return lookup;
};

const extractCollection = (response, fallback = []) => {
    const payload = response?.data;

    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;

    return fallback;
};

export const buildCommaSeparatedTextFromSelections = (selections, categories) => {
    if (!selections || typeof selections !== 'object') {
        return '';
    }

    const lookup = lookupFromCategories(categories);
    const deliveryCategoryKeys = new Set(['delivery_pacing', 'tone_emotional_style', 'pacing_delivery']);
    const deliverySubcategoryKeys = new Set([
        'tone',
        'pacing',
        'interaction_style',
        'relationship_archetypes',
        'creator_mode',
    ]);
    const ignoredCategoryKeys = new Set(['voice_cast']);
    const ignoredSubcategoryKeys = new Set(['voices']);
    const deliveryLabels = [];
    const structureLabels = [];

    const humanize = value =>
        value
            .replaceAll('_', ' ')
            .replace(/\s+/g, ' ')
            .trim()
            .replace(/^\w/, char => char.toUpperCase());

    (categories || []).forEach(category => {
        if (ignoredCategoryKeys.has(category.key)) {
            return;
        }

        const categorySelection = selections?.[category.key];
        if (!categorySelection || typeof categorySelection !== 'object') {
            return;
        }

        (category.subcategories || []).forEach(subcategory => {
            if (ignoredSubcategoryKeys.has(subcategory.key)) {
                return;
            }

            const selected = categorySelection?.[subcategory.key];
            if (selected === undefined || selected === null || selected === '') {
                return;
            }

            const keys = Array.isArray(selected) ? selected : [selected];
            keys.forEach(key => {
                if (typeof key !== 'string') return;
                const isDelivery =
                    deliveryCategoryKeys.has(category.key) || deliverySubcategoryKeys.has(subcategory.key);
                const label = lookup[key] || humanize(key);
                if (isDelivery) {
                    if (!deliveryLabels.includes(label)) {
                        deliveryLabels.push(label);
                    }
                    return;
                }

                if (!structureLabels.includes(label)) {
                    structureLabels.push(label);
                }
            });
        });
    });

    if (deliveryLabels.length === 0 && structureLabels.length === 0) {
        return '';
    }

    const humanList = values => {
        if (values.length === 1) return values[0];
        if (values.length === 2) return `${values[0]} and ${values[1]}`;
        return `${values.slice(0, -1).join(', ')}, and ${values[values.length - 1]}`;
    };

    const parts = [];
    if (deliveryLabels.length) {
        parts.push(`Performance direction: ${humanList(deliveryLabels)}`);
    }
    if (structureLabels.length) {
        parts.push(`Episode plan: ${humanList(structureLabels)}`);
    }

    return `${parts.join('. ')}. Keep transitions natural and maintain a consistent two-host flow.`;
};

export function usePodcastConfiguration() {
    const categories = computed(() => optionsCache.value || []);

    const load = async () => {
        if (Array.isArray(optionsCache.value) && optionsCache.value.length > 0) return;

        isLoading.value = true;
        loadingError.value = null;

        try {
            const optionsResponse = await axios.get('/api/podcast-configuration/options');
            const nextOptions = extractCollection(optionsResponse, []);

            if (Array.isArray(nextOptions) && nextOptions.length > 0) {
                optionsCache.value = nextOptions;
                return;
            }

            optionsCache.value = [];
            loadingError.value = 'Unable to load podcast configuration options.';
        } catch (error) {
            loadingError.value = error?.response?.data?.message || 'Unable to load podcast configuration options.';
        } finally {
            isLoading.value = false;
        }
    };

    const buildCommaSeparatedText = selections => buildCommaSeparatedTextFromSelections(selections, categories.value);

    return {
        categories,
        loading: isLoading,
        error: loadingError,
        load,
        buildCommaSeparatedText,
    };
}
